/**
 * TYPES
 */
import {ICustomerAdd} from 'src/aggregates/customers/commands.d';
import {ICustomerAddSearchTerm} from 'src/aggregates/customers/commands.d';
import {ICustomerBlockWhatsappContact}
    from 'src/aggregates/customers/commands.d';
import {ICustomerCreate} from 'src/aggregates/customers/commands.d';
import {ICustomerDropSiblings} from 'src/aggregates/customers/commands.d';
import {ICustomerFetch} from 'src/aggregates/customers/commands.d';
import {ICustomerFetchByTagPage} from 'src/aggregates/customers/commands.d';
import {ICustomerFetchContactTypes} from 'src/aggregates/customers/commands.d';
import {ICustomerFocus} from 'src/aggregates/customers/commands.d';
import {ICustomerLoadPage} from 'src/aggregates/customers/commands.d';
import {ICustomerLoadSiblings} from 'src/aggregates/customers/commands.d';
import {ICustomerModify} from 'src/aggregates/customers/commands.d';
import {ICustomerRemove} from 'src/aggregates/customers/commands.d';
import {ICustomerResetError} from 'src/aggregates/customers/commands.d';
import {ICustomerTag} from 'src/aggregates/customers/commands.d';
import {ICustomerUnblockWhatsappContact}
    from 'src/aggregates/customers/commands.d';
import {ICustomerUntag} from 'src/aggregates/customers/commands.d';
import {ICustomerUpdate} from 'src/aggregates/customers/commands.d';
import {ICustomerUpdateTagsFilter} from 'src/aggregates/customers/commands.d';
import {_names} from 'src/aggregates/customers/commands.d';
import {IContactPatch} from 'src/aggregates/customers/mutations.d';
import {IContact} from 'src/aggregates/customers/state.d';
import {ICustomerAgent} from 'src/aggregates/customers/state.d';
import {ICustomerTag as ICustomerTagData}
    from 'src/aggregates/customers/state.d';
import {_aggregates} from 'src/aggregates/index.d';


/**
 * CODE
 */

/**
 * I am a command to add a customer.
 *
 * :param agents: customer preferred agents
 * :param contacts: customer contacts
 * :param id: customer id
 * :pram name: customer name
 * :param unassociated: whether the contact is associated to a real customer
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param city: city of the customer
 * :param code: customer code
 * :param description: customer description
 * :param document: document of the customer
 * :param email: email of the customer
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param tags: customer tags
 * :param zipcode: zipcode of the customer
 *
 * :return: add customer command
 */
function add (
    agents: ICustomerAgent[],
    contacts: IContact[],
    id: number | string,
    name: string,
    unassociated: boolean,
    address1?: string,
    address2?: string,
    city?: string,
    code?: string,
    description?: string,
    document?: string,
    email?: string,
    organization?: string,
    state?: string,
    tags?: ICustomerTagData[],
    zipcode?: string,
): ICustomerAdd
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_ADD,
        _target: id,
        address1,
        address2,
        agents,
        city,
        code,
        contacts,
        description,
        document,
        email,
        name,
        organization,
        state,
        tags,
        unassociated,
        zipcode,
    };
}


/**
 * I am a command to set a customer search term.
 *
 * :param term: search term
 *
 * :return: add customer search term command
 */
function addSearchTerm (term: string): ICustomerAddSearchTerm
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_ADD_SEARCH_TERM,
        _target: null,
        term,
    };
}


/**
 * I am a command to block a whatsapp contact.
 *
 * :param account: whatsapp account uid
 * :param contact: contact uid
 *
 * :return: block whatsapp contact command
 */
function blockWhatsappContact (
    account: string,
    contact: string,
): ICustomerBlockWhatsappContact
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_BLOCK_WHATSAPP_CONTACT,
        _target: null,
        account,
        contact,
    };
}


/**
 * I am a command creator for customer create.
 *
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param city: city of the customer
 * :param code: code of the customer
 * :param company: company of the customer
 * :param contacts: contacts of the customer
 * :param name: name of the customer
 * :param description: description of the customer
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param zipcode: zipcode of the customer
 *
 * :returns: customer create command
 */
const create = (
    address1: string,
    address2: string,
    city: string,
    code: string,
    company: number,
    contacts: IContact[],
    document: string,
    name: string,
    organization: string,
    description: string,
    state: string,
    zipcode: string,
): ICustomerCreate => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_CREATE,
    _target: null,
    address1,
    address2,
    city,
    code,
    company,
    contacts,
    description,
    document,
    name,
    organization,
    state,
    zipcode,
});


/**
 * I am a command to drop the siblings of a contact that left the screen.
 *
 * :param id: contact id
 *
 * :returns: drop siblings command
 */
function dropSiblings (id: IContact['id']): ICustomerDropSiblings
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_DROP_SIBLINGS,
        _target: id,
    };
}


/**
 * I am a command creator for customer fetch.
 *
 * :param id: customer id to be fetched
 *
 * :returns: customer fetch command
 */
function fetch (id: number): ICustomerFetch
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_FETCH,
        _target: id,
    };
}


/**
 * I am a command creator for customer fetch by tag with pagination.
 *
 * :param tag: tag id
 *
 * :returns: customer fetch by tag page command
 */
function fetchByTagPage (tag: number): ICustomerFetchByTagPage
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_FETCH_BY_TAG_PAGE,
        _target: null,
        tag,
    };
}


/**
 * I am a command creator for customer fetch contact types.
 *
 * :returns: customer fetch contact types command
 */
const fetchContactTypes = (): ICustomerFetchContactTypes => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_FETCH_CONTACT_TYPES,
    _target: null,
});


/**
 * I am a command creator for customer focus.
 *
 * :param customer: customer id or path string or nothing
 *
 * :returns: customer focus command
 */
function focus (
    customer: string | number | null,
): ICustomerFocus
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_FOCUS,
        _target: null,
        customer,
    };
}


/**
 * I am a command creator for customer load page.
 *
 * :returns: customer load page command
 */
function loadPage (): ICustomerLoadPage
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_LOAD_PAGE,
        _target: null,
    };
}


/**
 * I am a command to load the siblings of the contact on screen.
 *
 * :param contact: contact on screen
 *
 * :returns: load siblings command
 */
function loadSiblings (contact: IContact): ICustomerLoadSiblings
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_LOAD_SIBLINGS,
        _target: contact.id,
        typeId: contact.type_id,
        uid: contact.value,
    };
}


/**
 * I am a command to modify a customer in the store.
 *
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param agents: customer preferred agents
 * :param city: city of the customer
 * :param code: customer code
 * :param contacts: customer contacts
 * :param description: customer description
 * :param document: document of the customer
 * :param id: customer id
 * :pram name: customer name
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param zipcode: zipcode of the customer
 * :param tags: customer tags
 *
 * :return: modify customer command
 */
function modify (
    address1: string,
    address2: string,
    agents: ICustomerAgent[],
    city: string,
    code: string,
    contacts: IContact[],
    description: string,
    document: string,
    id: number | string,
    name: string,
    organization: string,
    state: string,
    zipcode: string,
    tags?: ICustomerTagData[],
): ICustomerModify
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_MODIFY,
        _target: id,
        address1,
        address2,
        agents,
        city,
        code,
        contacts,
        description,
        document,
        name,
        organization,
        state,
        tags,
        zipcode,
    };
}


/**
 * I am a command creator for customer remove.
 *
 * :param id: customer id
 *
 * :returns: customer remove command
 */
const remove = (id: number): ICustomerRemove => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_REMOVE,
    _target: id,
});


/**
 * I am a command creator for customer reset error.
 *
 * :returns: customer reset error command
 */
const resetError = (): ICustomerResetError => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_RESET_ERROR,
    _target: null,
});


/**
 * I am a command creator for customer tag.
 *
 * :param id: customer id
 * :param tag: tag id
 *
 * :returns: customer tag command
 */
const tag = (id: number, tag: number): ICustomerTag => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_TAG,
    _target: id,
    tag,
});


/**
 * I am a command to unblock a whatsapp contact.
 *
 * :param account: whatsapp account uid
 * :param contact: contact uid
 *
 * :return: unblock whatsapp contact command
 */
function unblockWhatsappContact (
    account: string,
    contact: string,
): ICustomerUnblockWhatsappContact
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_UNBLOCK_WHATSAPP_CONTACT,
        _target: null,
        account,
        contact,
    };
}


/**
 * I am a command creator for customer untag.
 *
 * :param id: customer id
 * :param customerTag: customer tag association id
 *
 * :returns: customer tag command
 */
const untag = (id: number, customerTag: number): ICustomerUntag => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_UNTAG,
    _target: id,
    customerTag,
});


/**
 * I am a command creator for customer update.
 *
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param agents: agents who attend the customer
 * :param city: city of the customer
 * :param code: code of the customer
 * :param company: company of the customer
 * :param contacts: contacts of the customer
 * :param id: customer id
 * :param name: name of the customer
 * :param description: description of the customer
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param zipcode: zipcode of the customer
 * :param ensureUniqueContacts: whether to refuse contacts of another customer
 *
 * :returns: customer update command
 */
const update = (
    address1: string,
    address2: string,
    agents: string[],
    city: string,
    code: string,
    company: number,
    contacts: IContactPatch[],
    document: string,
    id: number,
    name: string,
    description: string,
    organization: string,
    state: string,
    zipcode: string,
    ensureUniqueContacts: boolean,
): ICustomerUpdate => ({
    _aggregate: _aggregates.CUSTOMERS,
    _name: _names.CUSTOMER_UPDATE,
    _target: id,
    address1,
    address2,
    agents,
    city,
    code,
    company,
    contacts,
    description,
    document,
    ensureUniqueContacts,
    name,
    organization,
    state,
    zipcode,
});


/**
 * I am a command creator for customer load unassociated contacts.
 *
 * :param company: company to load contacts from
 *
 * :returns: customer load unassociated contacts command
 */
function updateTagsFilter (
    tags: number[],
): ICustomerUpdateTagsFilter
{
    return {
        _aggregate: _aggregates.CUSTOMERS,
        _name: _names.CUSTOMER_UPDATE_TAGS_FILTER,
        _target: null,
        tags,
    };
}


/**
 * EXPORTS
 */
export {
    add,
    addSearchTerm,
    blockWhatsappContact,
    create,
    dropSiblings,
    fetch,
    fetchByTagPage,
    fetchContactTypes,
    focus,
    loadPage,
    loadSiblings,
    modify,
    remove,
    resetError,
    tag,
    unblockWhatsappContact,
    untag,
    update,
    updateTagsFilter,
};
